import { useEffect, useMemo, useState } from "react";
import {
    ShieldCheck, DownloadSimple, CheckCircle, Hourglass, Coins,
    CalendarBlank, FunnelSimple, Receipt, Stack, ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR } from "../../lib/format";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import RoleGuard from "../../components/RoleGuard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";

// ─────────────────────────────────────────────────────────────────────
// Indian FY helpers (mirror backend; Apr→Mar)
// ─────────────────────────────────────────────────────────────────────
function currentFy() {
    const now = new Date();
    const y = now.getFullYear();
    const start = now.getMonth() >= 3 ? y : y - 1;
    return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
}

function fyOptions() {
    const cur = currentFy();
    const startYear = parseInt(cur.split("-")[0], 10);
    return Array.from({ length: 6 }, (_, i) => {
        const s = startYear - i + 1;
        return { value: `${s - 1}-${String(s % 100).padStart(2, "0")}`, label: `FY ${s - 1}-${String(s % 100).padStart(2, "0")}` };
    });
}

const DEPOSITED_OPTIONS = [
    { value: "all", label: "All" },
    { value: "no", label: "Pending only" },
    { value: "yes", label: "Deposited only" },
];

export default function VendorTdsCompliance() {
    const [fy, setFy] = useState(currentFy());
    const [deposited, setDeposited] = useState("all");
    const [vendorId, setVendorId] = useState("");
    const [data, setData] = useState(null);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);
    const [depositRow, setDepositRow] = useState(null);

    const load = async () => {
        try {
            const params = { fy };
            if (vendorId) params.vendor_id = vendorId;
            if (deposited && deposited !== "all") params.deposited = deposited;
            const [r, v] = await Promise.all([
                api.get("/billing/reports/vendor-compliance/tds", { params }),
                vendors.length ? Promise.resolve({ data: { items: vendors } }) : api.get("/vendor-services?status=active").catch(() => ({ data: { items: [] } })),
            ]);
            setData(r.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load TDS compliance report"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Hydrate vendor dropdown from vendors list (one-time)
        api.get("/vendors").then((r) => {
            setVendors(r.data?.items || []);
        }).catch(() => { });
    }, []);

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [fy, deposited, vendorId]);

    const submitDeposit = async (payload) => {
        if (!depositRow) return;
        setSavingId(depositRow.id);
        try {
            const url = depositRow.source === "vendor_bill"
                ? `/vendor-bills/${depositRow.id}/tds-deposit`
                : `/vendor-service-invoices/${depositRow.id}/tds-deposit`;
            await api.patch(url, payload);
            toast.success(payload.tds_deposited ? "TDS marked as deposited" : "TDS deposit reverted");
            setDepositRow(null);
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Update failed"));
        } finally {
            setSavingId(null);
        }
    };

    const csv = () => {
        if (!data?.items?.length) return;
        const cols = ["FY", "Source", "Code", "Vendor Code", "Vendor", "Txn No", "Txn Date",
            "TDS Section", "TDS Rate %", "Gross Amount", "TDS Amount",
            "Deposited", "Deposit Date", "Challan No"];
        const rows = data.items.map((r) => [
            r.fy, r.source_label, r.code, r.vendor_code, r.vendor_name,
            r.txn_number, r.txn_date,
            r.tds_section, r.tds_rate, r.gross_amount, r.tds_amount,
            r.tds_deposited ? "Yes" : "No", r.tds_deposit_date || "", r.tds_challan_number || "",
        ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
        const blob = new Blob([cols.join(",") + "\n" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `vendor-tds-compliance-${fy}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading && !data) {
        return (
            <div data-testid="vendor-tds-page-loading">
                <PageHeader eyebrow="Compliance · TDS" title="Loading…" />
            </div>
        );
    }
    if (!data) return null;

    const s = data.summary || {};

    return (
        <div data-testid="vendor-tds-compliance-page">
            <PageHeader
                eyebrow={`Compliance · Vendor TDS Deposits · FY ${fy}`}
                title={
                    <span className="flex items-center gap-3">
                        <ShieldCheck size={22} weight="duotone" className="text-amber-600" />
                        Vendor TDS Compliance
                    </span>
                }
                description="Lightweight tracking of TDS deducted by REEDOS from vendor bills and service invoices, with optional deposit + challan capture for accounts."
                actions={
                    <Button variant="outline" size="sm" onClick={csv} disabled={!data.items.length} className="gap-1.5" data-testid="vendor-tds-export-csv-btn">
                        <DownloadSimple size={13} /> Export CSV
                    </Button>
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* Summary cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="vendor-tds-summary">
                    <Kpi label="Total TDS Deducted" value={formatINR(s.total_tds_deducted || 0)}
                         sub={`${s.txn_count || 0} transaction${s.txn_count === 1 ? "" : "s"}`}
                         icon={<Coins size={11} weight="fill" />} tone="amber" testid="kpi-total-deducted" />
                    <Kpi label="Total TDS Deposited" value={formatINR(s.total_tds_deposited || 0)}
                         sub="Challans filed"
                         icon={<CheckCircle size={11} weight="fill" />} tone="emerald" testid="kpi-total-deposited" />
                    <Kpi label="Pending TDS Deposit" value={formatINR(s.total_tds_pending || 0)}
                         sub="Awaiting government payment"
                         icon={<Hourglass size={11} weight="fill" />} tone="rose" testid="kpi-total-pending" />
                    <Kpi label="Pending Transactions" value={String(s.pending_count || 0)}
                         sub={`of ${s.txn_count || 0} total`}
                         icon={<Stack size={11} weight="fill" />} tone="indigo" testid="kpi-pending-count" />
                </div>

                {/* Filter bar */}
                <div className="reedos-card p-4" data-testid="vendor-tds-filters">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                        <FilterSelect label="Financial Year" value={fy} onChange={setFy} options={fyOptions()} testid="filter-fy" />
                        <FilterSelect label="Deposit Status" value={deposited} onChange={setDeposited} options={DEPOSITED_OPTIONS} testid="filter-deposited" />
                        <FilterSelect label="Vendor"
                            value={vendorId}
                            onChange={setVendorId}
                            options={[{ value: "", label: "All vendors" }, ...vendors.map((v) => ({ value: v.id, label: `${v.code || ""} · ${v.name}` }))]}
                            testid="filter-vendor" />
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => { setFy(currentFy()); setDeposited("all"); setVendorId(""); }} className="gap-1.5" data-testid="filter-reset-btn">
                                <ArrowCounterClockwise size={12} /> Reset
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Per-vendor rollup */}
                {data.by_vendor && data.by_vendor.length > 0 && (
                    <div className="reedos-card overflow-hidden" data-testid="vendor-tds-by-vendor">
                        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                            <CalendarBlank size={14} className="text-slate-500" />
                            <div className="font-display font-bold text-sm">Per-vendor rollup</div>
                            <div className="text-[11px] text-slate-500 ml-2">Sorted by total deducted (desc)</div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr className="text-left">
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Vendor</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Transactions</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">TDS Deducted</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">TDS Deposited</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Pending</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Pending #</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.by_vendor.map((v) => (
                                        <tr key={v.vendor_id || "u"} className="border-t border-slate-100 reedos-row-hover" data-testid={`vendor-tds-rollup-row-${v.vendor_id}`}>
                                            <td className="px-4 py-2.5 text-xs">
                                                <div className="font-semibold text-slate-900">{v.vendor_name || "—"}</div>
                                                <div className="font-mono text-[10px] text-slate-500">{v.vendor_code}</div>
                                            </td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-600">{v.txn_count}</td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data font-bold text-amber-700">{formatINR(v.tds_deducted)}</td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data text-emerald-700">{formatINR(v.tds_deposited)}</td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data text-rose-700">{formatINR(v.tds_pending)}</td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-600">{v.pending_count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Detail grid */}
                <div className="reedos-card overflow-hidden" data-testid="vendor-tds-detail-grid">
                    <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                        <FunnelSimple size={14} className="text-slate-500" />
                        <div className="font-display font-bold text-sm">Detailed transactions</div>
                        <div className="text-[11px] text-slate-500 ml-2">
                            {data.items.length} row{data.items.length === 1 ? "" : "s"} · Vendor Bills + Service Invoices
                        </div>
                    </div>
                    {data.items.length === 0 ? (
                        <EmptyState title="No TDS deductions found" description="No vendor bills or service invoices with TDS were recorded for this filter combination." />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr className="text-left">
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Source</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Code</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Vendor</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Txn No · Date</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Section</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Gross</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">TDS</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Deposit</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.items.map((r) => (
                                        <tr key={`${r.source}-${r.id}`} className="border-t border-slate-100 reedos-row-hover" data-testid={`vendor-tds-row-${r.id}`}>
                                            <td className="px-4 py-2.5 text-xs">
                                                <span className={classJoin("inline-block px-1.5 py-0.5 rounded font-semibold text-[10px]",
                                                    r.source === "vendor_bill" ? "bg-indigo-50 text-indigo-700 border border-indigo-200" : "bg-violet-50 text-violet-700 border border-violet-200")}>
                                                    {r.source_label}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2.5 text-xs font-mono font-semibold text-slate-900">{r.code}</td>
                                            <td className="px-4 py-2.5 text-xs">
                                                <div className="font-semibold text-slate-900 truncate max-w-[180px]">{r.vendor_name}</div>
                                                <div className="font-mono text-[10px] text-slate-500">{r.vendor_code}</div>
                                            </td>
                                            <td className="px-4 py-2.5 text-xs">
                                                <div className="font-mono text-slate-700">{r.txn_number}</div>
                                                <div className="text-[10px] text-slate-500">{r.txn_date}</div>
                                            </td>
                                            <td className="px-4 py-2.5 text-xs">
                                                <div className="font-mono text-slate-700">{r.tds_section || "—"}</div>
                                                <div className="text-[10px] text-slate-500">{r.tds_rate ? `${r.tds_rate}%` : ""}</div>
                                            </td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data text-slate-700">{formatINR(r.gross_amount)}</td>
                                            <td className="px-4 py-2.5 text-xs text-right reedos-data font-bold text-amber-700">{formatINR(r.tds_amount)}</td>
                                            <td className="px-4 py-2.5 text-xs">
                                                {r.tds_deposited ? (
                                                    <div data-testid={`deposit-status-${r.id}`}>
                                                        <span className="inline-block px-1.5 py-0.5 rounded font-semibold text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                            <CheckCircle size={9} weight="fill" className="inline mr-1" />Deposited
                                                        </span>
                                                        <div className="text-[10px] text-slate-500 mt-0.5">{r.tds_deposit_date}</div>
                                                        <div className="font-mono text-[10px] text-slate-700">{r.tds_challan_number}</div>
                                                    </div>
                                                ) : (
                                                    <span data-testid={`deposit-status-${r.id}`} className="inline-block px-1.5 py-0.5 rounded font-semibold text-[10px] bg-rose-50 text-rose-700 border border-rose-200">
                                                        <Hourglass size={9} weight="fill" className="inline mr-1" />Pending
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2.5 text-xs text-right">
                                                <RoleGuard permission="vendor:write">
                                                    <Button size="sm" variant="outline"
                                                        onClick={() => setDepositRow(r)}
                                                        disabled={savingId === r.id}
                                                        className="text-[10px] h-6 px-2"
                                                        data-testid={`deposit-btn-${r.id}`}>
                                                        {r.tds_deposited ? "Edit" : "Mark Deposited"}
                                                    </Button>
                                                </RoleGuard>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-slate-50 border-t border-slate-200">
                                    <tr className="font-semibold">
                                        <td colSpan={5} className="px-4 py-3 text-right reedos-eyebrow">Total</td>
                                        <td className="px-4 py-3 text-right reedos-data text-slate-700">—</td>
                                        <td className="px-4 py-3 text-right reedos-data text-amber-700 font-bold">{formatINR(s.total_tds_deducted || 0)}</td>
                                        <td colSpan={2}></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Deposit toggle dialog */}
            <DepositDialog row={depositRow} onClose={() => setDepositRow(null)} onSubmit={submitDeposit} saving={!!savingId} />
        </div>
    );
}

function DepositDialog({ row, onClose, onSubmit, saving }) {
    const [depositDate, setDepositDate] = useState("");
    const [challan, setChallan] = useState("");

    useEffect(() => {
        if (row) {
            setDepositDate(row.tds_deposit_date || new Date().toISOString().slice(0, 10));
            setChallan(row.tds_challan_number || "");
        }
    }, [row]);

    if (!row) return null;

    const handleSubmit = () => {
        if (!depositDate || !challan.trim()) {
            toast.error("Deposit date and challan number are both required");
            return;
        }
        onSubmit({
            tds_deposited: true,
            tds_deposit_date: depositDate,
            tds_challan_number: challan.trim(),
        });
    };

    const handleRevert = () => {
        if (!confirm("Revert this TDS deposit? Challan + date will be cleared.")) return;
        onSubmit({ tds_deposited: false });
    };

    return (
        <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="sm:max-w-md" data-testid="tds-deposit-dialog">
                <DialogHeader>
                    <DialogTitle>{row.tds_deposited ? "Edit TDS Deposit" : "Mark TDS Deposited"}</DialogTitle>
                    <DialogDescription>
                        {row.source_label} · <span className="font-mono">{row.code}</span> · Vendor {row.vendor_name}
                        <br />TDS Amount: <span className="font-mono font-bold text-amber-700">{formatINR(row.tds_amount)}</span>
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                    <div>
                        <Label htmlFor="deposit-date" className="text-xs">Deposit Date *</Label>
                        <Input id="deposit-date" type="date" value={depositDate} onChange={(e) => setDepositDate(e.target.value)} data-testid="deposit-date-input" />
                    </div>
                    <div>
                        <Label htmlFor="challan-no" className="text-xs">Challan Number *</Label>
                        <Input id="challan-no" placeholder="e.g. CIN/CRN-2025-001234" value={challan} onChange={(e) => setChallan(e.target.value)} data-testid="challan-number-input" />
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    {row.tds_deposited && (
                        <Button variant="outline" onClick={handleRevert} disabled={saving} className="mr-auto text-rose-700" data-testid="deposit-revert-btn">
                            Revert Deposit
                        </Button>
                    )}
                    <Button variant="outline" onClick={onClose} disabled={saving} data-testid="deposit-cancel-btn">Cancel</Button>
                    <Button onClick={handleSubmit} disabled={saving} data-testid="deposit-submit-btn">
                        {saving ? "Saving…" : (row.tds_deposited ? "Update" : "Mark Deposited")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Kpi({ label, value, sub, icon, tone = "slate", testid }) {
    const TONE = {
        slate: "text-slate-900",
        emerald: "text-emerald-700",
        rose: "text-rose-700",
        amber: "text-amber-700",
        indigo: "text-indigo-700",
        violet: "text-violet-700",
    };
    return (
        <div className="reedos-card p-4" data-testid={testid}>
            <div className="reedos-eyebrow flex items-center gap-1.5">{icon} {label}</div>
            <div className={classJoin("font-display font-bold reedos-data text-2xl mt-1 leading-none", TONE[tone])}>{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-1 leading-tight">{sub}</div>}
        </div>
    );
}

function FilterSelect({ label, value, onChange, options, testid }) {
    return (
        <div>
            <Label className="text-[10px] font-semibold text-slate-500 uppercase">{label}</Label>
            <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-8 px-2 text-xs border border-slate-200 rounded bg-white" data-testid={testid}>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
    );
}
